import * as Highcharts from 'highcharts';
import AccessibilityModule from 'highcharts/modules/accessibility';
import ExportModule from 'highcharts/modules/exporting';
import HighchartsMore from 'highcharts/highcharts-more';
import OfflineExportingModule from 'highcharts/modules/offline-exporting';

AccessibilityModule(Highcharts);
HighchartsMore(Highcharts);
ExportModule(Highcharts);
OfflineExportingModule(Highcharts);

export const adjustPlotLineLabels = (chart: Highcharts.Chart, line: Highcharts.YAxisPlotLinesOptions) => {
  if (!line.label || typeof line.value !== 'number') {
    return;
  }

  const { yAxis, plotHeight, plotWidth, inverted = false } = chart;
  const lineValuePixels = yAxis[0].toPixels(line.value, inverted);

  if (inverted) {
    if (lineValuePixels >= plotWidth) {
      line.label.x = -targetLineDefaults.chartLabelOffsets.xLabelOffset;
      line.label.align = 'right';
      return;
    }
    if (lineValuePixels <= targetLineDefaults.targetLabelWidth) {
      line.label.x = targetLineDefaults.chartLabelOffsets.xLabelOffset;
      line.label.align = 'left';
      return;
    }
  } else {
    if (lineValuePixels < targetLineDefaults.targetLabelHeight) {
      line.label.y = targetLineDefaults.chartLabelOffsets.yLabelBottomOffset;
      line.label.verticalAlign = 'bottom';
      return;
    }
    if (lineValuePixels > plotHeight - targetLineDefaults.targetLabelHeight) {
      line.label.y = -targetLineDefaults.chartLabelOffsets.yLabelTopOffset;
      line.label.verticalAlign = 'top';
      return;
    }
  }
};

export const redrawPlotLines = (chart: Highcharts.Chart): Highcharts.YAxisPlotLinesOptions[] => {
  const plotLines = chart.yAxis[0]?.options?.plotLines;
  if (!plotLines || plotLines.length === 0) {
    return [];
  }

  plotLines.forEach((line) => {
    adjustPlotLineLabels(chart, line);
  });

  const exportingYAxis = chart.options.exporting?.chartOptions?.yAxis;
  const exportingOptions = Array.isArray(exportingYAxis) ? exportingYAxis[0] : exportingYAxis;

  if (exportingOptions) {
    exportingOptions.plotLines = plotLines;
  }

  return plotLines;
};

export function getHighchartsDefaults() {
  return {
    title: {
      text: '',
      align: 'left',
      style: {
        fontSize: '1rem'
      }
    },
    subtitle: {
      text: '',
      align: 'left',
      style: {
        fontSize: '0.875rem'
      }
    },
    credits: {
      enabled: false
    },
    chart: {
      borderRadius: 4,
      style: {
        fontFamily: '"Benton Sans", Arial, sans-serif'
      },
      spacingTop: 30,
      events: {
        load: function () {
          const plotLines = redrawPlotLines(this);
          this.yAxis[0].update({ plotLines });
        }
      }
    },
    legend: {
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'middle',
      y: 50,
      x: 0
    },
    tooltip: {
      useHTML: true,
      outside: true
    },
    plotOptions: {
      series: {
        // keeping animation off to prevent slowness with large datasets
        animation: false,
        stickyTracking: false,
        enableMouseTracking: true
      },
      pie: {
        cursor: 'pointer',
        showInLegend: true,
        borderWidth: 0
      },
      gauge: {
        dataLabels: {
          align: 'center',
          borderWidth: 0,
          enabled: true,
          position: 'center',
          useHTML: true,
          style: { color: '#131517' }
        },
        dial: {
          radius: '80%',
          backgroundColor: gaugeDefaults.color.dial,
          baseWidth: 12,
          baseLength: '0%',
          rearLength: '0%'
        },
        pivot: {
          backgroundColor: gaugeDefaults.color.dial,
          radius: 6
        }
      },
      column: {
        pointPadding: 0,
        borderWidth: 0
      }
    },
    exporting: {
      sourceWidth: 1200,
      sourceHeight: 800
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: twoColumnMaxWidth
          },
          chartOptions: {
            legend: {
              layout: 'horizontal',
              align: 'left',
              verticalAlign: 'bottom',
              maxHeight: 64
            }
          }
        }
      ]
    }
  } as Highcharts.Options;
}

export function mergeWithDefaults(options: Highcharts.Options) {
  return Highcharts.merge(getHighchartsDefaults(), options);
}

export const DEFAULT_LOCALE = 'en-US';
export const DEFAULT_CURRENCY = 'USD';

export const gaugeDefaults = {
  color: {
    leftBand: '#6A92D4',
    rightBand: '#B5B9BD',
    targetCrossed: '#992F3A',
    targetCrossedTick: '#131517',
    dial: '#4D4F51'
  },
  thickness: {
    default: 20,
    extra: 2
  }
};

export const targetLineDefaults = {
  targetLineStyle: {
    color: '#9A9DA1',
    dashStyle: 'Dash' as Highcharts.DashStyleValue,
    zIndex: 5
  },
  targetLineLabelStyle: {
    color: '#616365',
    fontSize: '0.75rem',
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
    borderRadius: '5px' as unknown as number,
    padding: '5px',
    textAlign: 'right'
  },
  chartLabelOffsets: {
    xLabelOffset: 4,
    yLabelTopOffset: 30,
    yLabelBottomOffset: 14
  },
  targetLabelWidth: 70,
  targetLabelHeight: 30,
  targetLabelPadding: 18
};

export const twoColumnMaxWidth = 650;
export const threeColumnMaxWidth = 450;
